import React from 'react';
import { useSelector } from 'react-redux';
import { FieldProps } from 'formik';
import {
  CardHcd,
  ConsultationReason,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { Box } from '@mui/material';
import { IconCenter, IconHome, IconVideoCamera } from '@maiia/design-system';
import Text from '../../atoms/Text/Text';
import RadioButton from '../../atoms/RadioButton/RadioButton';
import Loader from '../../atoms/Loader/Loader';
import { useTranslation } from '@/src/i18n';
import { hasOnlyTlcOffer } from '../../utils/card';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { ConsultationReasonPublicType } from '@/src/types/enums';
import {
  PHYSICAL,
  PHYSICAL_OR_TELECONSULTATION,
  TELECONSULTATION,
  DIRECT_TELECONSULTATION,
  HOME_VISIT,
} from '@/src/constants';

const buttons = [
  {
    entitled: 'common:form_consultationtype_physical',
    value: PHYSICAL,
    iconComponent: <IconCenter />,
  },
  {
    entitled: 'common:form_consultationtype_tlc',
    value: TELECONSULTATION,
    iconComponent: <IconVideoCamera />,
  },
];

const ConsultationType = ({ type }: { type: ConsultationReasonPublicType }) => {
  const { t } = useTranslation();

  let label: string | undefined;
  let IconComponent: JSX.Element | undefined;

  if ([TELECONSULTATION, DIRECT_TELECONSULTATION].includes(type)) {
    label = t('rdv-happening-tlc');
    IconComponent = <IconVideoCamera aria-label={t('tlc_accessibility')} />;
  }

  if (type === PHYSICAL) {
    label = t('rdv-happening-physical');
    IconComponent = <IconCenter aria-label={t('physical_appointment')} />;
  }

  if (type === HOME_VISIT) {
    label = t('home-visit');
    IconComponent = <IconHome aria-label={t('home-visit')} />;
  }

  if (!label) {
    return null;
  }

  return (
    <div className="form-block--info">
      <Text variant="paragraph" component="p" iconComponent={IconComponent}>
        {label}
      </Text>
    </div>
  );
};

const ConsultationTypeChoice = ({
  field,
  form: { setFieldValue, values },
  card,
}: FieldProps & { card: CardHcd }) => {
  const { t } = useTranslation();
  const consultationsReasons = useSelector<any, ConsultationReason[]>(
    state => state.consultationReasonsByName?.items,
  );

  const isLoading = useSelector<any, boolean>(
    state => state?.consultationReasonsByName?.isLoading,
  );

  let typeReason = consultationsReasons?.find(
    consultation => consultation?.name === values?.consultationReasonName,
  )?.consultationType;
  if (hasOnlyTlcOffer(card)) typeReason = TELECONSULTATION;

  const [choice, setChoice] = React.useState<string | undefined>();

  const onChange = React.useCallback(
    e => {
      analyticsEvent({
        category: 'Booking',
        action: 'SelectConsultationType',
        label: e.target.name,
      });
      setChoice(e.target.name);
      setFieldValue(field.name, e.target.name);
    },
    [field.name],
  );

  React.useEffect(() => {
    const type =
      typeReason === PHYSICAL_OR_TELECONSULTATION ? undefined : typeReason;

    // TODO(nampnq): Fix when reset change practitioner
    if (typeof field?.value !== 'undefined') return;
    setFieldValue(field.name, type);
    setChoice(type);
  }, [typeReason, field?.value]);

  if (isLoading) {
    return (
      <Box textAlign="center">
        <Loader size={20} color="secondary" />
      </Box>
    );
  }

  return (
    <>
      {typeReason && <ConsultationType type={typeReason} />}
      {typeReason === PHYSICAL_OR_TELECONSULTATION && (
        <div className="wrapper--radio-buttons">
          {buttons.map(b => (
            <RadioButton
              {...b}
              entitled={t(b.entitled)}
              key={b.value}
              id={b.value}
              name={b.value}
              choice={choice}
              onChange={onChange}
              variant="block"
            />
          ))}
        </div>
      )}
    </>
  );
};

export default ConsultationTypeChoice;
